import type { Router, RouteRecordRaw } from 'vue-router'

import {
  CUSTOMER_PANEL_PATH,
  customerPanelChildMeta,
  customerPanelParentMeta,
  toCustomerPanelChildRoute,
} from './customerPanelRouteMeta'

const loading = new WeakMap<Router, Promise<boolean>>()
const ready = new WeakSet<Router>()

const loadLayoutCp = () => import('@/layouts/LayoutCP.vue')

/** `/customer-panel` and `/{locale}/customer-panel/...` — not `/customer-panel-extra`. */
export const isCustomerPanelPath = (path: string): boolean => {
  const pathname = path.split('?')[0]?.split('#')[0] ?? ''

  return /^(?:\/[a-z]{2})?\/customer-panel(?:\/|$)/.test(pathname)
}

/**
 * Named children stay on the stub so search can `push({ name: 'cp-favorites' })`
 * and the account drawer can render without loading CP page modules.
 */
export const customerPanelRouteStub: RouteRecordRaw = {
  children: [
    ...customerPanelChildMeta.map((child) =>
      toCustomerPanelChildRoute(child, loadLayoutCp),
    ),
    {
      path: ':pathMatch(.*)*',
      redirect: { name: 'cp-account-settings' },
    },
  ],
  component: loadLayoutCp,
  meta: customerPanelParentMeta,
  name: 'customer-panel',
  path: CUSTOMER_PANEL_PATH,
  redirect: { name: 'cp-account-settings' },
}

export const ensureCustomerPanelRoutes = async (
  router: Router,
): Promise<boolean> => {
  if (ready.has(router)) {
    return false
  }

  const pending = loading.get(router)

  if (pending) {
    return pending
  }

  const promise = (async () => {
    if (router.hasRoute('customer-panel')) {
      router.removeRoute('customer-panel')
    }

    const { customerPanelRoute } = await import('./customerPanelRoutes')

    router.addRoute(customerPanelRoute)
    ready.add(router)

    return true
  })()

  loading.set(router, promise)

  return promise
}
